import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AppSetting,
  AppSettingList,
  CreateAppSettingInput,
  UpdateAppSettingInput,
  CreateAppSettingListInput,
  UpdateAppSettingListInput,
} from '@peles/shared';
import * as appSettingsApi from '../api/app-settings';

// ─── App Settings ───────────────────────────────────────────────────────────

export function useAppSettings() {
  return useQuery<AppSetting[]>({
    queryKey: ['settings', 'appSettings'],
    queryFn: () => appSettingsApi.fetchAppSettings(),
  });
}

export function useCreateAppSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAppSettingInput) => appSettingsApi.createAppSetting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appSettings'] });
    },
  });
}

export function useUpdateAppSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateAppSettingInput }) =>
      appSettingsApi.updateAppSetting(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appSettings'] });
    },
  });
}

export function useDeleteAppSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => appSettingsApi.deleteAppSetting(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appSettings'] });
    },
  });
}

// ─── App Setting Lists ──────────────────────────────────────────────────────

export function useAppSettingLists() {
  return useQuery<AppSettingList[]>({
    queryKey: ['settings', 'appSettingLists'],
    queryFn: () => appSettingsApi.fetchAppSettingLists(),
  });
}

export function useCreateAppSettingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAppSettingListInput) => appSettingsApi.createAppSettingList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appSettingLists'] });
    },
  });
}

export function useUpdateAppSettingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateAppSettingListInput }) =>
      appSettingsApi.updateAppSettingList(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appSettingLists'] });
    },
  });
}

export function useDeleteAppSettingList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => appSettingsApi.deleteAppSettingList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appSettingLists'] });
    },
  });
}
